import { useState } from 'react';

export default function HowItWorksFaq() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: 'How do you ensure I\'m getting a quality vehicle?',
      answer: `Every vehicle goes through a thorough multi-point inspection before purchase. We provide full vehicle history reports and complete condition disclosures — so you know exactly what you're getting before you sign. No surprises.`
    },
    {
      question: 'Is delivery included in the price?',
      answer: `Delivery cost is often included in your financing or purchase price. The exact cost depends on distance and vehicle type. We'll provide a clear breakdown before you commit — no surprises.`
    },
    {
      question: 'How do wet signatures work for online purchases?',
      answer: `Most paperwork is signed digitally. Anything requiring a physical signature (like title transfers in certain states) is overnighted to you via FedEx. You sign, send it back, and we handle the rest. No dealership visit required.`
    },
    {
      question: "What's the $995 broker fee cover?",
      answer: `Our flat $995 broker fee covers nationwide vehicle search, inspection, history verification, negotiation on your behalf, and coordination of delivery. No hidden costs, no percentage markups — just one transparent fee.`
    },
    {
      question: 'Can you really source any vehicle from any dealership?',
      answer: `Yes. We have access to dealership networks, auctions, independent lots, and private sellers across all 50 states. If it's for sale somewhere, we can find it, inspect it, negotiate it, and deliver it to you.`
    },
    {
      question: 'How long does the online buying process take?',
      answer: `Most online purchases complete within 3-7 days. Financing approval is instant, paperwork takes 1-2 days, and delivery is scheduled within 3-5 days depending on your location.`
    },
    {
      question: 'How long does the broker process take?',
      answer: `Most broker deals complete within 7-14 days. Vehicle search takes 2-5 days, inspection and negotiation 2-4 days, and delivery is scheduled within 3-5 days. Complex requests may take longer.`
    },
    {
      question: 'What credit scores do you accept?',
      answer: `We work with all credit types — excellent, good, fair, and rebuilding. We partner with multiple lenders to find the best rate for your situation. Apply online to see your options instantly.`
    },
    {
      question: 'Do I have to use your financing?',
      answer: `No. You can use your own financing from a bank or credit union, pay cash, or use our financing partners. We'll work with whatever option is best for you.`
    },
    {
      question: 'Can I test drive before buying?',
      answer: `For vehicles in our inventory, yes — we can arrange a test drive. For broker deals where we're sourcing from another state, we conduct a thorough inspection and provide detailed photos/video before purchase. You approve everything before we buy.`
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          <p className="text-xl text-gray-600">
            Everything you need to know about buying online or using our broker service.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                aria-expanded={openIndex === index}
                aria-controls={`faq-panel-hiw-${index}`}
                id={`faq-btn-hiw-${index}`}
                className="w-full px-6 py-5 text-left flex items-center justify-between gap-4 cursor-pointer hover:bg-gray-50 transition"
              >
                <span className="hiw-faq-question font-bold text-gray-900 text-lg">{faq.question}</span>
                <i
                  className={`ri-arrow-down-s-line text-2xl text-[#4A5568] transition-transform flex-shrink-0 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                  aria-hidden="true"
                ></i>
              </button>
              {openIndex === index && (
                <div
                  id={`faq-panel-hiw-${index}`}
                  role="region"
                  aria-labelledby={`faq-btn-hiw-${index}`}
                  className="hiw-faq-answer px-6 pb-5 text-[#374151] leading-relaxed border-t border-gray-100 pt-4"
                >
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 bg-gradient-to-r from-teal-600 to-orange-600 p-8 rounded-2xl text-white text-center">
          <h3 className="text-2xl font-bold mb-3">Still Have Questions?</h3>
          <p className="text-lg mb-6">
            Our team is here to help. Reach out and we'll walk you through the entire process.
          </p>
          <a 
            href="/contact" 
            className="bg-white text-teal-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition whitespace-nowrap inline-flex items-center gap-2 cursor-pointer"
          >
            <i className="ri-customer-service-2-line"></i>
            Contact Us
          </a>
        </div>
      </div>
    </section>
  );
}
